import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#applyColorMediaFilter();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  #normalizeColor(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('tr-TR')
      .replace(/ı/g, 'i')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  #applyColorMediaFilter() {
    const selectedColor = this.#normalizeColor(this.dataset.selectedMediaColor);
    const groupedElements = Array.from(
      this.querySelectorAll('[data-media-color], [data-media-global], [data-media-color-unresolved]')
    );
    if (!selectedColor || !groupedElements.length || groupedElements.some((element) => element.hasAttribute('data-media-color-unresolved'))) {
      this.dataset.mediaColorFilter = selectedColor ? 'fallback' : 'not-applicable';
      return;
    }

    const selectedElements = groupedElements.filter((element) =>
      element.hasAttribute('data-media-global') || this.#normalizeColor(element.dataset.mediaColor) === selectedColor
    );
    if (!selectedElements.length) {
      this.dataset.mediaColorFilter = 'fallback';
      return;
    }

    for (const element of groupedElements) {
      const visible = element.hasAttribute('data-media-global') || this.#normalizeColor(element.dataset.mediaColor) === selectedColor;
      element.toggleAttribute('hidden', !visible);
      element.setAttribute('aria-hidden', `${!visible}`);
      if (element instanceof HTMLButtonElement) {
        if (!visible) {
          element.dataset.mediaPreviousTabindex = element.getAttribute('tabindex') || '';
          element.tabIndex = -1;
        } else if (element.dataset.mediaPreviousTabindex) {
          element.setAttribute('tabindex', element.dataset.mediaPreviousTabindex);
          delete element.dataset.mediaPreviousTabindex;
        } else {
          element.removeAttribute('tabindex');
        }
      }
    }

    for (const controls of this.querySelectorAll('slideshow-controls')) {
      const visibleControls = Array.from(
        controls.querySelectorAll('[data-media-color], [data-media-global]')
      ).filter((element) => !element.hasAttribute('hidden'));
      const total = controls.querySelector('[data-media-counter-total]');
      if (total) total.textContent = String(selectedElements.filter((element) => element.matches('slideshow-slide')).length);
      visibleControls.forEach((element, index) => {
        element.setAttribute('on:click', `/select/${index}`);
        element.setAttribute(
          'aria-label',
          `${index + 1} / ${visibleControls.length}`
        );
        element.textContent = element.matches('.slideshow-controls__dots button') ? String(index + 1) : element.textContent;
      });
    }

    this.dataset.mediaColorFilter = 'active';
    queueMicrotask(() => this.slideshow?.select(0, undefined, { animate: false }));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
